/**
 * Model CTA post-processing + visitor display helpers (TSAI `chat-complete` parity).
 */

import {
  extractAssistantPayload,
  looksLikeJsonAttempt,
  parseChatModelJson,
} from '@/lib/engage/chat-json-repair';

export type ResolvedModelCta = {
  label: string;
  url: string;
};

export type SiteCtaConfig = {
  ctaEnabled: boolean;
  ctaLabel: string;
  ctaUrl: string;
};

function isHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

/** Apply TSAI server-side CTA injection to a parsed model JSON object. */
export function applyModelCtaToParsedObject(
  parsed: Record<string, unknown>,
  site: SiteCtaConfig,
): Record<string, unknown> {
  const next = { ...parsed };
  const wantsCta = next.cta === true || (next.cta != null && next.cta !== false);

  if (wantsCta && site.ctaEnabled && site.ctaUrl) {
    next.cta = { label: site.ctaLabel, url: site.ctaUrl };
    return next;
  }

  if (next.cta != null) {
    delete next.cta;
  }
  return next;
}

/** Parse raw model output, inject CTA metadata, return serialized JSON (or original on parse miss). */
export function applyModelCtaPostProcessToRawText(rawText: string, site: SiteCtaConfig): string {
  const parsed = parseChatModelJson(rawText);
  if (!parsed.ok) return rawText;
  return JSON.stringify(applyModelCtaToParsedObject(parsed.value, site));
}

/** Visitor-visible assistant text — extracts `response.text` from structured JSON when present. */
export function assistantDisplayText(raw: string): string {
  const extracted = extractAssistantPayload(raw);
  if (!extracted.parseHadFailure) {
    return extracted.responseText;
  }
  const maybeJson = parseChatModelJson(raw);
  if (maybeJson.ok) {
    return extractAssistantPayload(JSON.stringify(maybeJson.value)).responseText;
  }
  // Incomplete stream or malformed JSON — avoid flashing raw structured output.
  if (looksLikeJsonAttempt(raw)) {
    return '';
  }
  return extracted.responseText;
}

/**
 * Resolve an inline CTA for the widget when the model signaled `cta: true`
 * (or the server already injected `{ label, url }`).
 */
export function resolveAssistantModelCta(
  raw: string,
  site: SiteCtaConfig,
): ResolvedModelCta | null {
  if (!site.ctaEnabled || !site.ctaUrl) return null;

  const parsed = parseChatModelJson(raw);
  if (!parsed.ok) return null;

  const cta = parsed.value.cta;
  if (cta === true) {
    return isHttpUrl(site.ctaUrl) ? { label: site.ctaLabel, url: site.ctaUrl } : null;
  }

  if (cta && typeof cta === 'object' && !Array.isArray(cta)) {
    const row = cta as Record<string, unknown>;
    const url = typeof row.url === 'string' && row.url.trim() ? row.url.trim() : site.ctaUrl;
    const label =
      typeof row.label === 'string' && row.label.trim() ? row.label.trim() : site.ctaLabel;
    return isHttpUrl(url) ? { label, url } : null;
  }

  return null;
}
